'use client';

import { useMemo } from 'react';
import { Moon, Calendar, CalendarCheck, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { DateInterval, CalendarDay } from '@/types/calendar';
import { formatShortDate, computeIntervalPrice } from '@/lib/calendar-utils';

interface BookingSummaryProps {
  interval: DateInterval;
  calendarDays: CalendarDay[];
  currency: string;
  onClear: () => void;
}

export function BookingSummary({
  interval,
  calendarDays,
  currency,
  onClear,
}: BookingSummaryProps) {
  const t = useTranslations('rooms');
  const { checkIn, checkOut } = interval;

  // Calculate price info for the interval
  const priceInfo = useMemo(() => {
    if (!checkIn || !checkOut) return null;

    const { nights, nightlyPrices, totalPrice, hasAllPrices } =
      computeIntervalPrice(checkIn, checkOut, calendarDays);

    const averagePrice = nights > 0 ? totalPrice / nights : 0;
    const minPrice = nightlyPrices.length > 0 ? Math.min(...nightlyPrices) : 0;
    const maxPrice = nightlyPrices.length > 0 ? Math.max(...nightlyPrices) : 0;
    const hasPriceVariation = minPrice !== maxPrice;

    return { nights, totalPrice, averagePrice, hasAllPrices, hasPriceVariation };
  }, [checkIn, checkOut, calendarDays]);

  // Format price with currency
  const formatPrice = (price: number) =>
    currency === 'EUR' ? `${Math.round(price)}` : `${Math.round(price)} ${currency}`;

  // Don't render if no selection started
  if (!checkIn) return null;

  return (
    <div
      className="w-64 md:w-80 p-2.5 md:p-4 rounded-xl border border-white/30"
      style={{ textShadow: '1px 1px 4px rgba(0, 0, 0, 0.5)', backgroundColor: 'rgba(113,113,113,0.45)', backdropFilter: 'blur(7px)' }}
    >
      {checkOut && priceInfo ? (
        <>
          {/* Info row - nights and average (avg only shown when every night is
              priced; an incomplete cross-month range would otherwise show a
              misleading average — see C6/C7) */}
          <div className="flex justify-between items-center text-xs md:text-sm text-white/70 mb-1.5 md:mb-3">
            <div className="flex items-center gap-1.5">
              <Moon size={14} className="text-amber-400" />
              <span>{t('nights', { count: priceInfo.nights })}</span>
            </div>
            {priceInfo.hasAllPrices && (
              <div className="flex items-center gap-1.5">
                <span>
                  {priceInfo.hasPriceVariation && `${t('avg')} `}{currency === 'EUR' ? '€' : ''}{formatPrice(priceInfo.averagePrice)}{t('perNightShort')}
                </span>
              </div>
            )}
          </div>

          {/* Date range */}
          <div className="flex items-center justify-between text-white text-sm md:text-base mb-1.5 md:mb-3">
            <div className="flex items-center gap-1.5 md:gap-2">
              <Calendar size={14} className="text-amber-400 md:w-4 md:h-4" />
              <span className="font-medium">{formatShortDate(checkIn)}</span>
            </div>
            <span className="text-white/50 mx-1.5 md:mx-2">→</span>
            <div className="flex items-center gap-1.5 md:gap-2">
              <CalendarCheck size={14} className="text-amber-400 md:w-4 md:h-4" />
              <span className="font-medium">{formatShortDate(checkOut)}</span>
            </div>
          </div>

          {/* Total and clear. The total figure is withheld (shows "—") unless
              every night in the range is priced (C7): summing only the loaded
              months would understate the real total next to the CTA. */}
          <div className="flex items-center justify-between pt-2 md:pt-3 border-t border-white/20">
            <div>
              <span className="text-white/50 text-xs uppercase tracking-wider">{t('total')}</span>
              <div className="text-xl md:text-2xl font-bold text-white">
                {priceInfo.hasAllPrices ? (
                  <>
                    {currency === 'EUR' ? '€' : ''}{formatPrice(priceInfo.totalPrice)}
                    {currency !== 'EUR' && <span className="text-lg ml-1">{currency}</span>}
                  </>
                ) : (
                  <span className="text-white/60">—</span>
                )}
              </div>
            </div>
            <button
              onClick={onClear}
              className="p-2.5 rounded-full bg-white hover:bg-red-100 shadow-lg transition-colors min-w-[40px] min-h-[40px] flex items-center justify-center"
              aria-label={t('clearSelection')}
            >
              <X size={20} className="text-red-600" />
            </button>
          </div>
        </>
      ) : (
        /* Check-in selected, awaiting check-out */
        <div className="text-center text-white/80">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Calendar size={16} className="text-amber-400" />
            <span className="font-medium">{t('checkIn')}: {formatShortDate(checkIn)}</span>
          </div>
          <p className="text-sm text-white/60">{t('selectCheckOut')}</p>
        </div>
      )}
    </div>
  );
}
